import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useReserveContext } from '../../../contexts/useReserveContext';
import { fetchStaffOptionsForDate } from '../../../utils/reservation/by-Date/fetchStaffOptionsForDate';
import { fetchOpenDatesForServiceReserve } from '../../../utils/reservation/by-service/fetchOpenDatesForServiceReserve';
import { fetchStaffServiceDurations } from '../../../utils/reservation/by-service/fetchStaffServiceDurations';
import { postAvailableTimesByService } from '../../../utils/reservation/by-service/postAvailableTimesByService';

// 共通UI
import ReserveStepHeader from '../ReserveStepHeader';
import ReserveCalendarGrid from '../ReserveCalendarGrid';
import { useI18n } from '../../../i18n';

const pad2 = (n: number) => String(n).padStart(2, '0');
const toMonthKeyFromYMD = (ymd?: string | null) => (ymd ? ymd.slice(0, 7) : null);
const toMonthKeyFromDate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

const SelectDateByService: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get('edit') === '1';
  const isRebooking = searchParams.get('rebook') === '1';
  const {
    storeId,
    selectedServices,
    selectedDate,
    selectedStartTime,
    selectedStaffId,
    isFreeReservation,
    setSelectedDate,
    setSelectedStaffId,
    setSelectedStaffName,
    setSelectedStartTime,
    setIsFreeReservation,
  } = useReserveContext();

  const serviceIds = useMemo(
    () =>
      (selectedServices || [])
        .map((s) => Number(s.serviceId))
        .filter((n) => Number.isFinite(n) && n > 0),
    [selectedServices]
  );

  const [openDates, setOpenDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 表示月
  const [month, setMonth] = useState<string>(() => {
    const m = toMonthKeyFromYMD(selectedDate);
    return m ?? toMonthKeyFromDate(new Date());
  });

  // guard + load
  useEffect(() => {
    if (!storeId) {
      toast.info(t('date.selectStoreFirst'));
      navigate('/reserve/by-service/store');
      return;
    }
    if (serviceIds.length === 0) {
      toast.info(t('date.selectServiceFirst'));
      navigate('/reserve/by-service/service');
      return;
    }
    if (isEditing && ((!selectedStaffId && !isFreeReservation) || !selectedStartTime)) {
      toast.info(t('date.returnAfterStaffTime'));
      navigate('/reserve/by-service/confirm-reservation');
      return;
    }
    if (isRebooking && !selectedStaffId) {
      toast.info(t('date.rebookingNotPrepared'));
      navigate('/reserve');
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const dates = await fetchOpenDatesForServiceReserve(storeId, serviceIds, 90);
        if (isEditing && isFreeReservation) {
          const filteredDates = await Promise.all(
            dates.map(async (date) => {
              const options = await fetchStaffOptionsForDate({
                storeId,
                date,
                startTime: selectedStartTime!,
                serviceIds,
              });
              return options.free_available ? date : null;
            }),
          );
          setOpenDates(filteredDates.filter((date): date is string => Boolean(date)));
        } else if (isEditing || isRebooking) {
          const durations = await fetchStaffServiceDurations(selectedStaffId!, serviceIds);
          const durationMap = new Map(
            durations.map((row) => [Number(row.service_id), Number(row.duration_minutes)]),
          );
          const durationMinutes = selectedServices.reduce(
            (sum, service) =>
              sum + (durationMap.get(Number(service.serviceId)) ?? Number(service.duration_minutes) ?? 0),
            0,
          );
          const filteredDates = await Promise.all(
            dates.map(async (date) => {
              const slots = await postAvailableTimesByService({
                store_id: storeId,
                staff_id: selectedStaffId!,
                date,
                duration_minutes: durationMinutes,
                service_ids: serviceIds,
              });
              if (isEditing) {
                return slots.some((slot) => slot.time === selectedStartTime && slot.available)
                  ? date
                  : null;
              }
              return slots.some((slot) => slot.available) ? date : null;
            }),
          );
          setOpenDates(filteredDates.filter((date): date is string => Boolean(date)));
        } else {
          setOpenDates(dates);
        }

        // 既に選択済みなら、その月に合わせる
        const m = toMonthKeyFromYMD(selectedDate);
        if (m) setMonth(m);
      } catch (e: any) {
        console.error(e);
        setError(e?.message || t('date.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [
    storeId,
    serviceIds,
    selectedServices,
    selectedDate,
    selectedStartTime,
    selectedStaffId,
    isFreeReservation,
    isEditing,
    isRebooking,
    navigate,
    t,
  ]);

  const openDateSet = useMemo(() => new Set(openDates), [openDates]);

  // 月移動の範囲（openDatesの最小/最大月）
  const { minMonth, maxMonth } = useMemo(() => {
    if (!openDates || openDates.length === 0) {
      return { minMonth: undefined as string | undefined, maxMonth: undefined as string | undefined };
    }
    const sorted = [...openDates].sort();
    return {
      minMonth: sorted[0].slice(0, 7),
      maxMonth: sorted[sorted.length - 1].slice(0, 7),
    };
  }, [openDates]);

  const handleSelectDate = (ymd: string) => {
    if (!openDateSet.has(ymd)) return;

    setSelectedDate(ymd);
    if (isEditing) {
      navigate('/reserve/by-service/confirm-reservation');
      return;
    }
    if (isRebooking) {
      setSelectedStartTime(null);
      setIsFreeReservation(false);
      navigate('/reserve/by-service/time?rebook=1');
      return;
    }

    // date が変わるので後続はリセット
    setSelectedStaffId(null);
    setSelectedStaffName(null);
    setSelectedStartTime(null);
    setIsFreeReservation(false);

    navigate('/reserve/by-service/time');
  };

  return (
    <div className="flex flex-col min-h-[70vh] space-y-4">
      <ReserveStepHeader
        title={t('date.chooseTitle')}
        onBack={() =>
          navigate(
            isEditing
              ? '/reserve/by-service/confirm-reservation'
              : isRebooking
              ? '/reserve'
              : '/reserve/by-service/service'
          )
        }
        step={isRebooking ? 1 : 3}
        totalSteps={isRebooking ? 3 : 5}
      />

      {loading ? (
        <p className="text-center text-slate-400 text-sm">{t('date.loadingCalendar')}</p>
      ) : error ? (
        <p className="text-center text-red-600 text-sm">{error}</p>
      ) : openDateSet.size === 0 ? (
        <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-rose-100 p-2 text-rose-600">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-rose-700">
                {isRebooking ? t('date.rebookingUnavailable') : t('date.cannotChange')}
              </div>
              <div className="mt-1 text-sm leading-6 text-rose-600">
                {isRebooking
                  ? t('date.noRebookingDates')
                  : t('date.noAlternativeService')}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <ReserveCalendarGrid
          month={month}
          onMonthChange={setMonth}
          selectedDate={selectedDate}
          openDateSet={openDateSet}
          onSelectDate={handleSelectDate}
          minMonth={minMonth}
          maxMonth={maxMonth}
          weekStartsOn={1}
        />
      )}
    </div>
  );
};

export default SelectDateByService;
